import classNames from 'classnames';
import React from 'react';
import * as styles from './PostTopicListItem.module.scss';
import HoverView from '../../components/HoverView';
import type { PopOverPrimitiveAllowControlledProps } from '../../components/PopOverPrimitive';

interface PostTopicListItemRootProps {
  children: React.ReactNode;
  size: 'small' | 'regular' | 'large';
  'aria-label'?: string;
  className?: string;
  style?: React.CSSProperties;
}

const PostTopicListItemRoot = React.forwardRef<
  HTMLLIElement,
  PostTopicListItemRootProps
>(({ children, className, style, 'aria-label': ariaLabel, size }, ref) => (
  <li
    ref={ref}
    aria-label={ariaLabel}
    style={style}
    className={classNames(className, styles.container, {
      [styles.large]: size === 'large',
      [styles.small]: size === 'small',
    })}
  >
    {children}
  </li>
));

interface PostTopicItemContentProps {
  name: React.ReactNode;
  description?: React.ReactNode;
  icon: React.ReactNode;
  setAnchorElement?: (element: HTMLElement | null) => void;
}

const PostTopicListItemContent = ({
  icon,
  name,
  description,
  setAnchorElement,
}: PostTopicItemContentProps) => (
  <div className={styles.contentContainer}>
    <div className={styles.icon}>{icon}</div>
    <div className={styles.content}>
      <div ref={setAnchorElement} className={styles.popOverAnchor} />
      {!!description && <div className={styles.description}>{description}</div>}
      <div className={styles.name}>{name}</div>
    </div>
  </div>
);

type PostTopicListItemPopOverProps = {
  anchorElement: HTMLElement | null;
  popOver: React.ReactNode;
  children: React.ReactNode;
} & PopOverPrimitiveAllowControlledProps;

const PostTopicListItemPopOver = ({
  anchorElement,
  children,
  popOver,
  ...props
}: PostTopicListItemPopOverProps) => {
  return (
    <HoverView
      anchorElement={anchorElement}
      hoverGroupId="post-topic-list-item"
      placement="bottom-start"
      hoverDelay="slow"
      view={popOver}
      {...props}
    >
      <div className={styles.popOverTrigger}>{children}</div>
    </HoverView>
  );
};

export default {
  Root: PostTopicListItemRoot,
  Content: PostTopicListItemContent,
  PopOver: PostTopicListItemPopOver,
};
